import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { ConfiguratorProductPrice } from "../../../components/ConfiguratorProductPrice";
import { Image } from "../../../components/Image";
import { RatingGraphic } from "../../../components/RatingGraphic";
import { RichText } from "../../../components/RichText";
import { IProduct } from "../../../models/catalogue";
import { DiscountDisplayMode } from "../../../models/discountDisplay";
import { isoProductID, isoSafeHTML } from "../../../models/nominals";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { usePageSetting } from "../../../utils/settings";
import { supTrademarks } from "../../../utils/supTrademarks";
import { IModularConfiguratorOptionSet } from "../models";

import styles from "./index.module.scss";

const getImage = (rootProduct: IProduct): IProduct["images"][0] | null => {
    if (rootProduct.images.length <= 0) {
        return null;
    }
    const image =
        rootProduct.images.length > 1
            ? rootProduct.images.find((img) => img.role === "PLP Config")
            : rootProduct.images[0];
    return image || null;
};

const getLowestPriceVariant = (rootProduct: IProduct): IProduct => {
    // First, check if this is a stand-alone product
    if (rootProduct.children.length <= 0) {
        return rootProduct;
    }
    // get lowest priced variant that is available to buy
    const variant = rootProduct.children.reduce((prev, curr) => {
        if (
            !prev.availability.is_available_to_buy &&
            curr.availability.is_available_to_buy
        ) {
            return curr;
        }
        if (!curr.availability.is_available_to_buy) {
            return prev;
        }
        const currPrice = getProductPrice(curr.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        const prevPrice = getProductPrice(prev.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        return currPrice.lessThan(prevPrice) ? curr : prev;
    }, rootProduct.children[0]);
    return variant;
};

const areAllVariantsOutOfStock = (rootProduct: IProduct): boolean => {
    // First, check if this is a stand-alone product
    if (rootProduct.children.length <= 0) {
        return !rootProduct.availability.is_available_to_buy;
    }
    const firstVariantAvailability =
        rootProduct.children[0].availability.is_available_to_buy;
    if (firstVariantAvailability) {
        return false;
    } else {
        return rootProduct.children.every(function (element) {
            return (
                element.availability.is_available_to_buy ===
                firstVariantAvailability
            );
        });
    }
};

const buildAboveRatingContent = (
    rootProduct: IProduct,
    appSlug: string | null | undefined,
): React.ReactNode => {
    if (rootProduct.attributes.configurator_panel_above_rating_text) {
        return (
            <RichText
                html={supTrademarks(
                    isoSafeHTML.wrap(
                        `${rootProduct.attributes.configurator_panel_above_rating_text.value}`,
                    ),
                    appSlug,
                )}
                inline={true}
            />
        );
    }
    if (rootProduct.description) {
        return (
            <RichText
                html={supTrademarks(rootProduct.description, appSlug)}
                inline={true}
            />
        );
    }
    return null;
};

const buildBelowRatingContent = (
    rootProduct: IProduct,
    appSlug: string | null | undefined,
): React.ReactNode => {
    if (rootProduct.attributes.configurator_panel_below_rating_text) {
        return (
            <RichText
                html={supTrademarks(
                    isoSafeHTML.wrap(
                        `${rootProduct.attributes.configurator_panel_below_rating_text.value}`,
                    ),
                    appSlug,
                )}
            />
        );
    }
    if (
        rootProduct.attributes.title_richtext &&
        rootProduct.attributes.title_richtext.value
    ) {
        return (
            <RichText
                html={supTrademarks(
                    isoSafeHTML.wrap(
                        `${rootProduct.attributes.title_richtext.value}`,
                    ),
                    appSlug,
                )}
                tagName="h2"
            />
        );
    }
    return null;
};

export const PanelRootProductSelectorOption = (
    props: Immutable<{
        optionSet: IModularConfiguratorOptionSet;
        rootProduct: IProduct;
        selectedRootProduct: IProduct;
        onSelect: () => void;
        onClosePanel: () => void;
        enableRatings: boolean;
    }>,
): React.ReactNode => {
    const appSlug = usePageSetting("app_slug");

    const handleRatingActivate = (): void => {
        props.onClosePanel();

        // Scroll and focus on the next tick since closing the modal will change focus also. We
        // need to make sure this happens after that.
        setTimeout(() => {
            const document = getDocument();
            const reviews = document?.querySelector<HTMLElement>(
                ".customer-review__button",
            );
            reviews?.scrollIntoView(true);
            reviews?.focus();
        }, 0);
    };

    const optionSelected =
        props.rootProduct.id === props.selectedRootProduct?.id;
    const optionId = `${props.rootProduct.id}`;
    const optionClass = classNames({
        [styles.radioOption]: true,
        [styles.activeOption]: optionSelected,
    });
    const optionContainerClass = classNames({
        "configurator__radio-option-container": true,
        "configurator__radio-option-container--option-model": true,
        "configurator__radio-option-container--disable":
            areAllVariantsOutOfStock(props.rootProduct),
        [styles.optionContainer]: true,
    });
    const variant = getLowestPriceVariant(props.rootProduct);
    const image = getImage(props.rootProduct);
    const aboveRatingContent = buildAboveRatingContent(
        props.rootProduct,
        appSlug,
    );
    const showRatings = !!(props.rootProduct.rating && props.enableRatings);

    return (
        <li>
            {props.rootProduct.attributes.tile_header &&
                props.rootProduct.attributes.tile_header.value && (
                    <RichText
                        className="rich-text configurator__radio-option-container--tile-header"
                        html={supTrademarks(
                            isoSafeHTML.wrap(
                                `${props.rootProduct.attributes.tile_header.value}`,
                            ),
                            appSlug,
                        )}
                        tagName="h3"
                    />
                )}
            <div className={optionContainerClass}>
                <input
                    type="radio"
                    id={optionId}
                    name={"option_model"}
                    tabIndex={optionSelected ? 0 : -1}
                    onChange={props.onSelect}
                    value={isoProductID.unwrap(props.rootProduct.id)}
                    checked={optionSelected}
                />
                {(aboveRatingContent || showRatings) && (
                    <div className={styles.ratingRow}>
                        {aboveRatingContent && (
                            <label
                                htmlFor={optionId}
                                className={styles.aboveRatingLabel}
                            >
                                {aboveRatingContent}
                            </label>
                        )}
                        {showRatings && (
                            <button
                                type="button"
                                aria-label={interpolate(t`View %s reviews`, [
                                    props.rootProduct.title,
                                ])}
                                onClick={handleRatingActivate}
                                className={styles.ratingButton}
                            >
                                <RatingGraphic
                                    rating={props.rootProduct.rating}
                                    numReviews={props.rootProduct.num_reviews}
                                    onlyReviewsNum={true}
                                    cardClass={"panel-model"}
                                    cardSize={"small"}
                                    starRatingClass={styles.ratingStars}
                                    reviewNumberClass={styles.reviewNumber}
                                />
                            </button>
                        )}
                    </div>
                )}
                <label
                    htmlFor={optionId}
                    className={optionClass}
                    aria-selected={optionSelected}
                >
                    <div className={styles.optionBody}>
                        <div className={styles.imgContainer}>
                            {image && (
                                <Image
                                    src={image.original}
                                    className="responsive-img"
                                    alt={props.rootProduct.title}
                                />
                            )}
                        </div>
                        <div className={styles.contentContainer}>
                            {buildBelowRatingContent(
                                props.rootProduct,
                                appSlug,
                            )}
                            <ConfiguratorProductPrice
                                configuratorType={props.optionSet.layout.design}
                                strikeThroughMSRP={false}
                                discountDisplayMode={
                                    DiscountDisplayMode.DISABLED
                                }
                                actualPriceStyle={""}
                                price={variant.price}
                                pricingMessage={t`Starting at`}
                                calloutCopy={
                                    props.rootProduct.attributes.product_callout
                                        ?.value
                                }
                                actualPriceClass={styles.priceActual}
                                messageClass={styles.priceMessage}
                                retailPriceClass={styles.priceRetail}
                            />
                        </div>
                    </div>
                </label>
            </div>
        </li>
    );
};
